// invoice-notifications.component.ts
import {
  Component, OnInit, OnDestroy, ViewChild, ElementRef, AfterViewInit
} from '@angular/core';
import { FormBuilder, FormGroup } from '@angular/forms';
import { HttpClient } from '@angular/common/http';
import { MatDialog } from '@angular/material/dialog';
import { MatTableDataSource } from '@angular/material/table';
import { MatPaginator } from '@angular/material/paginator';
import { MatSort } from '@angular/material/sort';
import { Subject } from 'rxjs';
import { takeUntil, finalize } from 'rxjs/operators';
import { Chart } from 'chart.js';

import { InvoiceNotificationsService } from './invoice-notifications.service';
import { InvoiceNotification, NotificationStats } from './invoice-notification.model';
import { JsonViewerDialogComponent } from '../dialogs/json-viewer-dialog/json-viewer-dialog.component';

@Component({
  selector: 'app-invoice-notifications',
  templateUrl: './invoice-notifications.component.html',
  styleUrls: ['./invoice-notifications.component.scss']
})
export class InvoiceNotificationsComponent implements OnInit, AfterViewInit, OnDestroy {

  // ── Tabella ──────────────────────────────────────────────────────────────
  displayedColumns = [
    'InvoiceBarcode', 'InvoiceId', 'Supplier', 'NotificationType', 'Recipient',
    'Status', 'Orders', 'Detail', 'SolicitCount', 'FirstSentAt', 'LastSolicitAt',
    'ResolvedAt', 'ResolutionDays'
  ];
  dataSource = new MatTableDataSource<InvoiceNotification>([]);
  @ViewChild(MatPaginator, { static: false }) paginator: MatPaginator;
  @ViewChild(MatSort,      { static: false }) sort: MatSort;

  // ── Charts ───────────────────────────────────────────────────────────────
  @ViewChild('typeChart',       { static: false }) typeChartRef: ElementRef;
  @ViewChild('solicitChart',    { static: false }) solicitChartRef: ElementRef;
  private typeChart:    Chart;
  private solicitChart: Chart;

  // ── Stato ────────────────────────────────────────────────────────────────
  stats: NotificationStats;
  loading = false;
  downloadingId: string | null = null;
  filterForm: FormGroup;

  readonly NOTIFICATION_TYPES = [
    { value: 'MISSING_ORDER_ID',        label: 'Numero ordine mancante'          },
    { value: 'MAINTENANCE',             label: 'Manutenzione'                    },
    { value: 'TAX_MISMATCH',            label: 'Mismatch Codice Fiscale'         },
    { value: 'VAT_MISMATCH',            label: 'Mismatch Partita IVA'            },
    { value: 'ORDER_NOT_FOUND',         label: 'Ordine non riconosciuto'         },
    { value: 'INVOICE_DETAIL_MISMATCH', label: 'Dettaglio fattura non riconosc.' },
    { value: 'GOODS_NOT_RECEIVED',      label: 'Ordine in stato incongruente'   },
  ];

  readonly TYPE_LABELS: { [key: string]: string } = {
    MISSING_ORDER_ID:        'N. ordine mancante',
    MAINTENANCE:             'Manutenzione',
    TAX_MISMATCH:            'Mismatch CF',
    VAT_MISMATCH:            'Mismatch P.IVA',
    ORDER_NOT_FOUND:         'Ordine non riconosc.',
    INVOICE_DETAIL_MISMATCH: 'Dettaglio non riconosc.',
    GOODS_NOT_RECEIVED:      'Ordine incongruente',
  };

  readonly TYPE_COLORS = ['#1976d2', '#388e3c', '#f57c00', '#d32f2f', '#7b1fa2', '#00796b', '#e65100'];

  private destroy$ = new Subject<void>();

  constructor(
    private svc:    InvoiceNotificationsService,
    private fb:     FormBuilder,
    private http:   HttpClient,
    private dialog: MatDialog
  ) {}

  ngOnInit() {
    this.filterForm = this.fb.group({
      status:    [''],
      type:      [''],
      from:      [null],
      to:        [null],
      recipient: [''],
      supplier:  [''],
    });

    this.load();
    this.loadStats();
  }

  ngAfterViewInit() {
    this.dataSource.paginator = this.paginator;
    this.dataSource.sort      = this.sort;
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
    if (this.typeChart)    { this.typeChart.destroy();    }
    if (this.solicitChart) { this.solicitChart.destroy(); }
  }

  // ── Caricamento dati ──────────────────────────────────────────────────────
  load() {
    const f = this.filterForm.value;
    this.loading = true;

    const filters: any = {};
    if (f.status)    { filters.status    = f.status; }
    if (f.type)      { filters.type      = f.type;   }
    if (f.from)      { filters.from      = (f.from as Date).toISOString(); }
    if (f.to)        { filters.to        = (f.to   as Date).toISOString(); }
    if (f.recipient) { filters.recipient = f.recipient; }
    if (f.supplier)  { filters.supplier  = f.supplier;  }

    this.svc.getAll(filters)
      .pipe(takeUntil(this.destroy$), finalize(() => this.loading = false))
      .subscribe(data => {
        this.dataSource.data = data;
      });
  }

  loadStats() {
    this.svc.getStats()
      .pipe(takeUntil(this.destroy$))
      .subscribe(stats => {
        this.stats = stats;
        setTimeout(() => {
          this.buildTypeChart(stats);
          this.buildSolicitChart(stats);
        }, 100);
      });
  }

  resetFilters() {
    this.filterForm.reset({ status: '', type: '', from: null, to: null, recipient: '', supplier: '' });
    this.load();
  }

  // ── Grafici ───────────────────────────────────────────────────────────────
  private buildTypeChart(stats: NotificationStats) {
    if (!this.typeChartRef) { return; }
    if (this.typeChart)     { this.typeChart.destroy(); }

    const labels = stats.byType.map(t => this.TYPE_LABELS[t.type] || t.type);
    const open   = stats.byType.map(t => t.open);
    const res    = stats.byType.map(t => t.resolved);

    this.typeChart = new Chart(this.typeChartRef.nativeElement, {
      type: 'bar',
      data: {
        labels,
        datasets: [
          { label: 'Aperte',   data: open, backgroundColor: '#f57c00' },
          { label: 'Risolte',  data: res,  backgroundColor: '#388e3c' }
        ]
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        legend: { position: 'bottom' },
        scales: {
          xAxes: [{ stacked: false }],
          yAxes: [{ stacked: false, ticks: { beginAtZero: true, stepSize: 1 } }]
        }
      }
    });
  }

  private buildSolicitChart(stats: NotificationStats) {
    if (!this.solicitChartRef) { return; }
    if (this.solicitChart)     { this.solicitChart.destroy(); }

    const labels   = stats.byType.map(t => this.TYPE_LABELS[t.type] || t.type);
    const solicits = stats.byType.map(t => t.avgSolicits);

    this.solicitChart = new Chart(this.solicitChartRef.nativeElement, {
      type: 'horizontalBar',
      data: {
        labels,
        datasets: [{
          label: 'Media solleciti',
          data: solicits,
          backgroundColor: this.TYPE_COLORS
        }]
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        legend: { display: false },
        scales: {
          xAxes: [{ ticks: { beginAtZero: true } }]
        }
      }
    });
  }

  // ── Download fattura ─────────────────────────────────────────────────────
  downloadInvoice(invoiceId: string) {
    if (!invoiceId || this.downloadingId === invoiceId) { return; }
    this.downloadingId = invoiceId;

    this.http.post<any>(
      'https://forecast.dentalpro.it/api/sign/fatturepassive/downloadpdf',
      { key: 'xxnejbU321sss387233.6j$!', id: invoiceId }
    ).pipe(
      takeUntil(this.destroy$),
      finalize(() => this.downloadingId = null)
    ).subscribe(
      res => {
        if (!res || !res.Base64File) {
          alert('File non disponibile');
          return;
        }
        const binary = atob(res.Base64File);
        const bytes  = new Uint8Array(binary.length);
        for (let i = 0; i < binary.length; i++) { bytes[i] = binary.charCodeAt(i); }
        const blob     = new Blob([bytes], { type: 'application/xml' });
        const url      = URL.createObjectURL(blob);
        const filename = res.Filename || (invoiceId + '.xml');
        const a        = document.createElement('a');
        a.href         = url;
        a.download     = filename;
        a.click();
        URL.revokeObjectURL(url);
      },
      err => { alert('Errore durante il download della fattura'); }
    );
  }

  // ── Helpers template ──────────────────────────────────────────────────────
  getTypeLabel(type: string): string {
    return this.TYPE_LABELS[type] || type;
  }

  getStatusColor(status: string): string {
    return status === 'OPEN' ? 'warn' : 'primary';
  }

  getStatusLabel(status: string): string {
    return status === 'OPEN' ? 'Aperta' : 'Risolta';
  }

  openDetail(raw: string) {
    let json: any;
    try { json = JSON.parse(raw); } catch { json = raw; }
    this.dialog.open(JsonViewerDialogComponent, {
      width: '80%',
      maxHeight: '80vh',
      data: { title: 'Dettaglio', json }
    });
  }

  trackById(index: number, item: InvoiceNotification): number {
    return item.Id;
  }
}
